/** Pull-plan entry: the Re-pull start page (repull.html mock). Opens right
 *  after a draft is created for a Re-pull. One rule, applied automatically:
 *  anything with actuals is kept and locked as history; everything not started
 *  is marked for replacement (struck, restorable). Two columns: the partition
 *  (leaf blocks) on the left, the pull-target picker on the right. "Start Plan"
 *  is gated until a target is chosen (a candidate, custom dates, or skip). */
import React from "react";
import {
  fetchPullEntryInsights,
  fetchPullPlanScopeMembers,
  getPullPlan,
  savePullPlan,
} from "../../../../api/clientV2";
import type {
  EntryInsights,
  PullTargetChoice,
  ScopeMember,
} from "../../../../models";
import Spinner from "../../../../components/Spinner/Spinner";
import { autoFontColor } from "../../../../utils/color";
import { apiErrorMessage } from "../../../../utils/apiError";
import { hashColor } from "../../PullPlanGrid/helpers";
import "./PartitionView.css";

/** "M/D" from an ISO date. */
const fmtMD = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const [, m, d] = iso.slice(0, 10).split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : null;
};

/** The date (or earliest–latest range) a pull-target candidate commits to,
 *  across its per-level dates. */
const targetDateLabel = (per_leaf: Array<{ date: string | null }>): string | null => {
  const ds = per_leaf.map((p) => p.date).filter((x): x is string => !!x).sort();
  if (ds.length === 0) return null;
  const lo = fmtMD(ds[0]);
  const hi = fmtMD(ds[ds.length - 1]);
  return lo === hi ? lo : `${lo}–${hi}`;
};

interface Props {
  scheduleId: number;
  draftId: number;
  sectionName: string;
  onBack: () => void;
  /** Proceed to the board with this draft. */
  onStartPlan: () => void;
}

const NOT_STARTED = "TK_NotStart";

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  TK_Complete: { text: "done", cls: "pe-status--complete" },
  TK_Active: { text: "in progress", cls: "pe-status--active" },
  TK_NotStart: { text: "not started", cls: "pe-status--idle" },
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}`;
}

function leafLabel(m: ScopeMember): string {
  const path = m.wbs_path ?? [];
  return path[path.length - 1] || `WBS ${m.wbs_node_id}`;
}

/** Strip the leaves' common name prefix so date columns read by what DIFFERS. */
function shortLeafLabel(name: string, allNames: string[]): string {
  if (allNames.length < 2) return name;
  let prefix = allNames[0];
  for (const n of allNames.slice(1)) {
    while (prefix && !n.startsWith(prefix)) prefix = prefix.slice(0, -1);
  }
  const short = name.slice(prefix.length).trim();
  return short || name;
}

const PartitionView: React.FC<Props> = ({
  scheduleId,
  draftId,
  sectionName,
  onBack,
  onStartPlan,
}) => {
  const [members, setMembers] = React.useState<ScopeMember[] | null>(null);
  const [insights, setInsights] = React.useState<EntryInsights | null>(null);
  const payloadRef = React.useRef<any>(null);
  const [deletedIds, setDeletedIds] = React.useState<Set<number>>(new Set());
  const [target, setTarget] = React.useState<PullTargetChoice | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const plan = await getPullPlan(scheduleId, draftId);
        const raw: any = JSON.parse(plan.payload_json || "{}");
        const wbsIds: number[] = raw.scope_wbs_ids ?? [];
        const [mems, ins] = await Promise.all([
          fetchPullPlanScopeMembers(scheduleId, wbsIds),
          fetchPullEntryInsights(scheduleId, wbsIds),
        ]);
        if (!live) return;
        if (!raw.entry_partition) {
          raw.pending_activity_deletes = mems
            .filter((m) => m.status === NOT_STARTED)
            .map((m) => ({ activity_id: m.activity_id }));
          raw.member_keeps = mems
            .filter((m) => m.status !== NOT_STARTED)
            .map((m) => m.activity_id);
          raw.entry_partition = { at: new Date().toISOString() };
          await savePullPlan(scheduleId, draftId, {
            payload_json: JSON.stringify(raw),
          });
        }
        if (!live) return;
        payloadRef.current = raw;
        setDeletedIds(
          new Set(
            (raw.pending_activity_deletes ?? []).map(
              (d: { activity_id: number }) => d.activity_id,
            ),
          ),
        );
        setTarget(raw.pull_target ?? null);
        setMembers(mems);
        setInsights(ins);
      } catch (e: unknown) {
        if (live) setError(apiErrorMessage(e, "Failed to open the draft."));
      }
    })();
    return () => {
      live = false;
    };
  }, [scheduleId, draftId]);

  const persist = React.useCallback(() => {
    const raw = payloadRef.current;
    if (!raw) return;
    savePullPlan(scheduleId, draftId, { payload_json: JSON.stringify(raw) }).catch(
      () => setError("Save failed — changes may not persist."),
    );
  }, [scheduleId, draftId]);

  const setFate = React.useCallback(
    (ids: number[], fate: "keep" | "replace") => {
      const raw = payloadRef.current;
      if (!raw) return;
      const idSet = new Set(ids);
      const deletes: Array<{ activity_id: number }> =
        raw.pending_activity_deletes ?? [];
      const keeps: number[] = raw.member_keeps ?? [];
      if (fate === "keep") {
        raw.pending_activity_deletes = deletes.filter(
          (d) => !idSet.has(d.activity_id),
        );
        raw.member_keeps = [...keeps.filter((k) => !idSet.has(k)), ...ids];
      } else {
        raw.pending_activity_deletes = [
          ...deletes.filter((d) => !idSet.has(d.activity_id)),
          ...ids.map((id) => ({ activity_id: id })),
        ];
        raw.member_keeps = keeps.filter((k) => !idSet.has(k));
      }
      setDeletedIds(
        new Set(
          raw.pending_activity_deletes.map(
            (d: { activity_id: number }) => d.activity_id,
          ),
        ),
      );
      persist();
    },
    [persist],
  );

  const chooseTarget = React.useCallback(
    (choice: PullTargetChoice) => {
      const raw = payloadRef.current;
      if (!raw) return;
      raw.pull_target = choice;
      setTarget(choice);
      persist();
    },
    [persist],
  );

  const leaves = React.useMemo(() => {
    if (!members) return [];
    const order: number[] = [];
    const byLeaf = new Map<number, ScopeMember[]>();
    for (const m of members) {
      if (!byLeaf.has(m.wbs_node_id)) {
        byLeaf.set(m.wbs_node_id, []);
        order.push(m.wbs_node_id);
      }
      byLeaf.get(m.wbs_node_id)!.push(m);
    }
    return order.map((id) => {
      const mems = [...byLeaf.get(id)!].sort((a, b) => {
        const s = (a.restart_date ?? "9999").localeCompare(b.restart_date ?? "9999");
        if (s !== 0) return s;
        return (a.reend_date ?? "9999").localeCompare(b.reend_date ?? "9999");
      });
      return { wbsId: id, label: leafLabel(mems[0]), members: mems };
    });
  }, [members]);

  const allLeafNames = React.useMemo(() => leaves.map((l) => l.label), [leaves]);

  const lockedCount = React.useMemo(
    () => (members ?? []).filter((m) => m.status !== NOT_STARTED).length,
    [members],
  );
  // The section's current date span (before the re-pull) — context for where the
  // work sits today vs the pull target you choose.
  const sectionRange = React.useMemo(() => {
    const starts = (members ?? []).map((m) => m.restart_date).filter((x): x is string => !!x);
    const ends = (members ?? []).map((m) => m.reend_date).filter((x): x is string => !!x);
    if (!starts.length || !ends.length) return null;
    starts.sort();
    ends.sort();
    return { start: starts[0], finish: ends[ends.length - 1] };
  }, [members]);

  // ---- pull-target picker (inline, right column) -------------------------
  const candidates = React.useMemo(() => insights?.candidates ?? [], [insights]);
  const [selected, setSelected] = React.useState<number | "custom" | null>(null);
  const [customDates, setCustomDates] = React.useState<Record<number, string>>({});

  // Default selection from the persisted target (once loaded).
  React.useEffect(() => {
    if (selected !== null || !insights) return;
    if (target?.mode === "custom") {
      const init: Record<number, string> = {};
      for (const e of target.per_leaf) init[e.wbs_id] = e.date;
      setCustomDates(init);
      setSelected("custom");
    } else if (target?.mode === "milestone") {
      const i = candidates.findIndex((c) => c.label === target.label);
      setSelected(i >= 0 ? i : 0);
    } else if (target?.mode === "skip") {
      setSelected(null);
    } else if (candidates.length > 0) {
      // Visual-only pre-select of the best fit — do NOT persist a target on
      // mount. Persisting here would let a "looked at the screen, backed out"
      // count as an accepted target, so Resume would skip the review gate.
      // Start Plan commits the selected candidate (see onStart).
      setSelected(0);
    }
  }, [insights, target, candidates, selected]);

  const pickCandidate = (i: number) => {
    setSelected(i);
    const c = candidates[i];
    if (c)
      chooseTarget({
        mode: "milestone",
        label: c.label,
        work_code: c.work_code,
        per_leaf: c.per_leaf,
      });
  };

  // Start Plan: commit the visually pre-selected best-fit candidate if the user
  // never actively picked one (target still null), so the default works in one
  // click without auto-persisting on mount.
  const handleStart = React.useCallback(() => {
    if (!target && typeof selected === "number") {
      const c = candidates[selected];
      if (c)
        chooseTarget({
          mode: "milestone",
          label: c.label,
          work_code: c.work_code,
          per_leaf: c.per_leaf,
        });
    }
    onStartPlan();
  }, [target, selected, candidates, chooseTarget, onStartPlan]);

  const setCustomDate = (wbsId: number, date: string) => {
    const next = { ...customDates, [wbsId]: date };
    setCustomDates(next);
    setSelected("custom");
    const per_leaf = leaves
      .filter((l) => next[l.wbsId])
      .map((l) => ({ wbs_id: l.wbsId, leaf_name: l.label, date: next[l.wbsId] }));
    if (per_leaf.length > 0) chooseTarget({ mode: "custom", per_leaf });
  };

  if (error) {
    return (
      <div className="pull-entry">
        <div className="pull-entry-error">{error}</div>
        <button type="button" className="pe-btn" onClick={onBack}>
          ← Back
        </button>
      </div>
    );
  }
  if (!members || !insights) {
    return (
      <div className="pull-entry">
        <Spinner visible label="Opening pull plan draft…" />
      </div>
    );
  }

  return (
    <div className="pull-entry">
      <div className="pull-entry-header">
        <button type="button" className="pull-entry-back" onClick={onBack}>
          ← Plans
        </button>
        <h1 className="pull-entry-h1">Re-pull · {sectionName}</h1>
        <span className="pull-entry-mode-chip">⟲ wipe &amp; rebuild</span>
      </div>
      <p className="pull-entry-lede">
        {leaves.length} WBS area{leaves.length === 1 ? "" : "s"} ·{" "}
        {members.length} activities. Reviewing what carries over before you
        rebuild.
      </p>

      <div className="pe-stats">
        <div className="pe-stat">
          <div className="pe-stat-n pe-stat-n--locked">{lockedCount}</div>
          <div className="pe-stat-l">Locked (actuals)</div>
        </div>
        <div className="pe-stat">
          <div className="pe-stat-n pe-stat-n--replace">{deletedIds.size}</div>
          <div className="pe-stat-l">To replace</div>
        </div>
        <div className="pe-stat">
          <div className="pe-stat-n">{insights.counts.external_ties}</div>
          <div className="pe-stat-l">External ties</div>
        </div>
        {sectionRange && (
          <div className="pe-stat">
            <div className="pe-stat-n pe-stat-n--range">
              {fmtMD(sectionRange.start)} → {fmtMD(sectionRange.finish)}
            </div>
            <div className="pe-stat-l">Current span</div>
          </div>
        )}
      </div>

      <div className="pe-grid">
        {/* LEFT — partition leaf blocks */}
        <div className="pe-partition">
          {leaves.map((leaf) => {
            const kept = leaf.members.filter(
              (m) => !deletedIds.has(m.activity_id),
            ).length;
            const toReplace = leaf.members.length - kept;
            const restorable = leaf.members
              .filter((m) => deletedIds.has(m.activity_id))
              .map((m) => m.activity_id);
            return (
              <div className="pe-leaf-block" key={leaf.wbsId}>
                <div className="pe-leaf-bar">
                  <span className="pe-leaf-name">{leaf.label}</span>
                  <span className="pe-leaf-tally">
                    {kept} kept · {toReplace} to replace
                  </span>
                  {restorable.length > 0 && (
                    <button
                      type="button"
                      className="pe-leaf-restore"
                      onClick={() => setFate(restorable, "keep")}
                    >
                      ↺ Restore all
                    </button>
                  )}
                </div>
                {leaf.members.map((m) => {
                  const deleted = deletedIds.has(m.activity_id);
                  const locked = m.status !== NOT_STARTED;
                  const st = STATUS_LABEL[m.status] ?? {
                    text: m.status,
                    cls: "pe-status--idle",
                  };
                  const wcColor = m.takt_work ? hashColor(m.takt_work) : "";
                  return (
                    <div
                      key={m.activity_id}
                      className={
                        "pe-prow" +
                        (deleted ? " pe-prow--replace" : " pe-prow--keep") +
                        (locked ? " pe-prow--locked" : "")
                      }
                    >
                      <span className="pe-prow-code">
                        {m.takt_work && (
                          <span
                            className="pe-wc-pill"
                            style={{
                              background: wcColor,
                              color: autoFontColor(wcColor),
                            }}
                          >
                            {m.takt_work}
                          </span>
                        )}
                      </span>
                      <span className="pe-prow-name">{m.task_name}</span>
                      <span
                        className="pe-prow-dates"
                        title="When this activity runs"
                      >
                        {fmtDate(locked ? m.actual_start : m.restart_date)} –{" "}
                        {fmtDate(locked ? m.actual_finish : m.reend_date)}
                      </span>
                      <span className={`pe-prow-state ${st.cls}`}>
                        {locked
                          ? `✓ ${st.text} · locked`
                          : deleted
                            ? "↻ replace"
                            : "kept"}
                      </span>
                      <span className="pe-prow-act">
                        {locked ? (
                          <span title="kept · locked">🔒</span>
                        ) : deleted ? (
                          <button
                            type="button"
                            className="pe-prow-btn"
                            title="Keep this one"
                            onClick={() => setFate([m.activity_id], "keep")}
                          >
                            ↺
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="pe-prow-btn"
                            title="Replace this one"
                            onClick={() => setFate([m.activity_id], "replace")}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div className="pe-untagged-note">
            ◔ Including the untagged work — a re-pull rebuilds the whole physical
            area, so everything not-started is in play; placing it on the board
            re-tags it cleanly.
          </div>
        </div>

        {/* RIGHT — pull-target picker (sticky) */}
        <div className="pe-target">
          <div className="pe-target-card">
            <div className="pe-target-head">
              🎯 Pull toward…
              <span className="pe-target-req">required</span>
            </div>
            <p className="pe-target-sub">
              The downstream commitment the rebuilt train pulls to. Ranked from
              your ties.
            </p>

            <div className="pe-target-list">
              {candidates.map((c, i) => (
                <button
                  key={`${c.label}-${i}`}
                  type="button"
                  className={
                    "pe-tgt" + (selected === i ? " pe-tgt--sel" : "")
                  }
                  onClick={() => pickCandidate(i)}
                >
                  <div className="pe-tgt-top">
                    {c.work_code && (
                      <span className="pe-tgt-code">{c.work_code}</span>
                    )}
                    <span className="pe-tgt-name">{c.label}</span>
                    {targetDateLabel(c.per_leaf) && (
                      <span className="pe-tgt-date" title="The date(s) this commitment lands on, across levels">
                        ▎ by {targetDateLabel(c.per_leaf)}
                      </span>
                    )}
                    {i === 0 && <span className="pe-tgt-best">★ best fit</span>}
                  </div>
                  <div className="pe-tgt-why">
                    fed by {c.fed_by_ties} in-section tie
                    {c.fed_by_ties === 1 ? "" : "s"}
                    {c.leaf_count > 1
                      ? ` · recurs on ${c.leaf_count} levels`
                      : ""}
                    {c.milestone_ish ? " · milestone" : ""}
                  </div>
                </button>
              ))}

              <button
                type="button"
                className={
                  "pe-tgt pe-tgt--custom" +
                  (selected === "custom" ? " pe-tgt--sel" : "")
                }
                onClick={() => setSelected("custom")}
              >
                <div className="pe-tgt-top">
                  <span className="pe-tgt-name">📅 Custom date per level</span>
                </div>
                <div className="pe-tgt-why">
                  commitment lives outside these leaves
                </div>
              </button>
              {selected === "custom" && (
                <div className="pe-target-custom">
                  {leaves.map((l) => (
                    <label key={l.wbsId} className="pe-custom-field">
                      {shortLeafLabel(l.label, allLeafNames)}
                      <input
                        type="date"
                        value={customDates[l.wbsId] ?? ""}
                        onChange={(e) => setCustomDate(l.wbsId, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="pe-target-skip"
                onClick={() => {
                  setSelected(null);
                  chooseTarget({ mode: "skip" });
                }}
              >
                Skip — set later
              </button>
            </div>

            <div className="pe-target-foot">
              <button
                type="button"
                className="pull-entry-start"
                disabled={!target && typeof selected !== "number"}
                title={
                  !target && typeof selected !== "number"
                    ? "Choose a pull target first"
                    : undefined
                }
                onClick={handleStart}
              >
                Start Plan →
              </button>
              <p className="pe-target-hint">
                {target
                  ? target.mode === "skip"
                    ? "No pull target — planning without a target box. Re-run entry to set one."
                    : "A target is chosen — you’re good to go."
                  : "Pick a target (or skip) to start."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartitionView;
